import { useEffect, useRef } from 'react';
import { editorViewCtx } from '@milkdown/kit/core';
import { useInstance } from '@milkdown/react';

import { isMilkdownContextNotFoundError } from '../../collaboration/constants';
import type { ReviewSuggestionsEditorData, ReviewSuggestionsLoader } from '../../types';
import { reviewSuggestionsDecoKey, reviewSuggestionsUpdateMeta } from './plugin';

/** Bound RAF retries while waiting for Milkdown `editorViewCtx`. ~10s at 60fps. */
const APPLY_SUGGESTIONS_RAF_RETRY_CAP = 600;

/**
 * Loads review suggestions in React once the Milkdown editor view exists, then
 * applies them via plugin meta. Unlike the previous `$proseAsync` loader, this
 * does not gate `editorViewCtx` registration on a network fetch — so the
 * toolbar and collab mount handlers can run against a live view while suggestions
 * load.
 *
 * Reloads preserve `selectedReviewSuggestionIdRef` when that id is still in the
 * loaded set so decoration selection stays aligned without relying on default
 * selection / sync effects re-firing.
 *
 * If `getInstance()` or `editorViewCtx` is briefly unavailable when the fetch
 * resolves, apply is retried on subsequent animation frames (same window as
 * `useReviewSuggestionBulkActionsRef`).
 */
export function useReviewSuggestionsLoader(
  reviewSuggestionsLoader: ReviewSuggestionsLoader | undefined,
  onReviewSuggestionsLoadedRef: React.MutableRefObject<((data: ReviewSuggestionsEditorData) => void) | undefined>,
  editorGenerationRef: React.MutableRefObject<number>,
  /** React-owned rail selection — must survive suggestion reloads (default-selection + sync won't re-fire). */
  selectedReviewSuggestionIdRef: React.MutableRefObject<string | null>,
): void {
  const [loadingInstance, getInstance] = useInstance();
  const fetchGenerationRef = useRef(0);

  useEffect(() => {
    if (loadingInstance || !reviewSuggestionsLoader) return undefined;

    let cancelled = false;
    let rafId: number | null = null;
    let rafAttempts = 0;
    fetchGenerationRef.current = editorGenerationRef.current;

    const tryApplySuggestions = (data: ReviewSuggestionsEditorData) => {
      if (cancelled || editorGenerationRef.current !== fetchGenerationRef.current) return;

      // Preserve React selection across reloads. `useDefaultReviewSuggestionSelection` skips
      // once applied while `hasReviewSuggestions` stays true, and selection sync only runs when
      // the React id changes — so hard-coding null here would leave decorations unselected.
      const currentSelectedId = selectedReviewSuggestionIdRef.current;
      const selectedId =
        currentSelectedId && data.reviewSuggestions.some((suggestion) => suggestion.id === currentSelectedId) ? currentSelectedId : null;

      let applied = false;
      try {
        const editor = getInstance();
        if (editor) {
          editor.action((ctx) => {
            const view = ctx.get(editorViewCtx);
            const tr = view.state.tr.setMeta(reviewSuggestionsDecoKey, reviewSuggestionsUpdateMeta(data.reviewSuggestions, selectedId));
            view.dispatch(tr);
            applied = true;
          });
        }
      } catch (error) {
        if (!isMilkdownContextNotFoundError(error)) throw error;
      }

      if (applied) {
        onReviewSuggestionsLoadedRef.current?.(data);

        return;
      }

      rafAttempts += 1;
      if (rafAttempts >= APPLY_SUGGESTIONS_RAF_RETRY_CAP) {
        console.warn(`useReviewSuggestionsLoader: stopped retrying suggestion apply after ${APPLY_SUGGESTIONS_RAF_RETRY_CAP} frames.`);

        return;
      }

      // Instance / editorViewCtx not ready yet — retry next frame until we succeed or unmount.
      rafId = requestAnimationFrame(() => {
        tryApplySuggestions(data);
      });
    };

    void reviewSuggestionsLoader
      .fetch()
      .then((raw) => {
        if (cancelled) return;
        tryApplySuggestions(reviewSuggestionsLoader.map(raw));
      })
      .catch(() => {
        if (cancelled) return;
        tryApplySuggestions({ reviewSuggestions: [] });
      });

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [
    loadingInstance,
    reviewSuggestionsLoader,
    getInstance,
    onReviewSuggestionsLoadedRef,
    editorGenerationRef,
    selectedReviewSuggestionIdRef,
  ]);
}
